import React from "react";
import { withTranslation } from "react-i18next";
import { connectAlita } from "redux-alita";
import { Form, Input, Button, Icon, message } from "antd";
import { getSiteConfigSupport, putSiteConfigSupport } from '../../axios';
import './index.less';

@withTranslation(["common"])
class UpdateContactInfoDynamic extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            technicalSupportPairs: [this.createRow()],
            salesInquiriesPairs: [this.createRow()],
            submitting: false,
        };
    }

    createRow = () => ({
        id: this.getRowId(),
        key: '',
        value: '',
    });

    getRowId = () => {
        if (window.crypto && window.crypto.randomUUID) {
            return window.crypto.randomUUID();
        }
        return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    };

    normalizePairs = (pairs) => {
        if (!Array.isArray(pairs)) {
            return [];
        }
        return pairs.map((item) => ({
            id: this.getRowId(),
            key: item && item.key ? String(item.key) : '',
            value: item && item.value ? String(item.value) : '',
        }));
    };

    getValidPairs = (pairs) => pairs
        .map((item) => ({
            key: (item.key || '').trim(),
            value: (item.value || '').trim(),
        }))
        .filter((item) => item.key && item.value);

    hasIncompletePairs = (pairs) => pairs.some((item) => {
        const key = (item.key || '').trim();
        const value = (item.value || '').trim();
        return (key && !value) || (!key && value);
    });

    addRow = (groupKey) => {
        this.setState((prevState) => ({
            [groupKey]: [...prevState[groupKey], this.createRow()],
        }));
    };

    removeRow = (groupKey, id) => {
        this.setState((prevState) => {
            const nextList = prevState[groupKey].filter((item) => item.id !== id);
            return {
                [groupKey]: nextList.length ? nextList : [this.createRow()],
            };
        });
    };

    updateRow = (groupKey, id, field, value) => {
        this.setState((prevState) => ({
            [groupKey]: prevState[groupKey].map((item) => (item.id === id ? { ...item, [field]: value } : item)),
        }));
    };

    handleSubmit = (e) => {
        e.preventDefault();
        const { t } = this.props;
        const { technicalSupportPairs, salesInquiriesPairs, submitting } = this.state;

        if (submitting) {
            return;
        }

        if (this.hasIncompletePairs(technicalSupportPairs) || this.hasIncompletePairs(salesInquiriesPairs)) {
            message.warn(t('UpdateContactInfoKeyValueRequired'));
            return;
        }

        const validTechnicalSupport = this.getValidPairs(technicalSupportPairs);
        const validSalesInquiries = this.getValidPairs(salesInquiriesPairs);

        if (!validTechnicalSupport.length && !validSalesInquiries.length) {
            message.warn(t('UpdateContactInfoAddAtLeastOne'));
            return;
        }

        this.setState({ submitting: true });
        putSiteConfigSupport({
            technicals: validTechnicalSupport,
            sales: validSalesInquiries,
        }).then((res) => {
            if (res.code === 100000) {
                message.success(res.msg || t('UpdateContactInfoSaveSuccess'));
            } else {
                message.warn(res.msg || t('UpdateContactInfoSaveFailed'));
            }
        }).catch(() => {
            message.error(t('UpdateContactInfoSaveFailed'));
        }).finally(() => {
            this.setState({ submitting: false });
        });
    };

    componentDidMount() {
        this.getSiteConfigSupport();
    }

    getSiteConfigSupport = () => {
        getSiteConfigSupport().then(res => {
            if (res.code === 100000 && res.data) {
                const technicalSupportPairs = this.normalizePairs(
                    res.data.technicals || res.data.technicalSupportPairs || res.data.technicalSupport || []
                );
                const salesInquiriesPairs = this.normalizePairs(
                    res.data.sales || res.data.salesInquiriesPairs || res.data.salesInquiries || []
                );

                this.setState({
                    technicalSupportPairs: technicalSupportPairs.length ? technicalSupportPairs : [this.createRow()],
                    salesInquiriesPairs: salesInquiriesPairs.length ? salesInquiriesPairs : [this.createRow()],
                });
            }
        });
    };

    renderPairGroup = (titleKey, groupKey, pairs) => {
        const { t } = this.props;
        const { submitting } = this.state;

        return (
            <div className="update-contact-info-dynamic__group-card">
                <div className="update-contact-info-dynamic__group-title">{t(titleKey)}</div>
                <Button
                    type="dashed"
                    className="update-contact-info-dynamic__add-button"
                    onClick={() => this.addRow(groupKey)}
                    disabled={submitting}
                >
                    {t('UpdateContactInfoAddRow')}
                </Button>

                {pairs.map((item) => (
                    <div
                        key={item.id}
                        className="update-contact-info-dynamic__pair-row"
                    >
                        <Input
                            value={item.key}
                            placeholder={t('UpdateContactInfoKey')}
                            onChange={(e) => this.updateRow(groupKey, item.id, 'key', e.target.value)}
                            disabled={submitting}
                        />
                        <Input
                            value={item.value}
                            placeholder={t('UpdateContactInfoValue')}
                            onChange={(e) => this.updateRow(groupKey, item.id, 'value', e.target.value)}
                            disabled={submitting}
                        />
                        <Button
                            type="link"
                            className="update-contact-info-dynamic__delete-button"
                            onClick={() => this.removeRow(groupKey, item.id)}
                            disabled={submitting}
                        >
                            {t('Delete')}
                        </Button>
                    </div>
                ))}
            </div>
        );
    };

    renderPreviewSection = (titleKey, descriptionKey, iconType, items) => {
        const { t } = this.props;

        return (
            <div className="update-contact-info-dynamic__preview-section">
                <div className="update-contact-info-dynamic__preview-header">
                    <div className="update-contact-info-dynamic__preview-icon-wrap">
                        <div className="update-contact-info-dynamic__preview-icon-circle">
                            <Icon type={iconType} className="update-contact-info-dynamic__preview-icon" />
                        </div>
                    </div>
                    <div className="update-contact-info-dynamic__preview-heading">
                        <div className="update-contact-info-dynamic__preview-title">{t(titleKey)}</div>
                        <div className="update-contact-info-dynamic__preview-description">{t(descriptionKey)}</div>
                    </div>
                </div>
                <div className="update-contact-info-dynamic__preview-items">
                    {items.map((item, idx) => (
                        <div key={`${titleKey}_${idx}`} className="update-contact-info-dynamic__preview-item">
                            <div className="update-contact-info-dynamic__preview-item-key" title={item.key}>
                                {item.key}:
                            </div>
                            <div className={`update-contact-info-dynamic__preview-item-value${item.value && item.value.length > 40 ? ' update-contact-info-dynamic__preview-item-value--compact' : ''}`}>
                                {item.value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    render() {
        const { t } = this.props;
        const { technicalSupportPairs, salesInquiriesPairs, submitting } = this.state;

        const validTechnicalSupport = this.getValidPairs(technicalSupportPairs);
        const validSalesInquiries = this.getValidPairs(salesInquiriesPairs);

        const hasTsData = validTechnicalSupport.length > 0;
        const hasSiData = validSalesInquiries.length > 0;
        const showPreview = hasTsData || hasSiData;
        const previewColumns = hasTsData && hasSiData ? '1fr 1fr' : '1fr';

        const formItemLayout = {
            labelCol: {
                xs: { span: 24 },
                sm: { span: 4 },
            },
            wrapperCol: {
                xs: { span: 24 },
                sm: { span: 20 },
            },
        };

        return (
            <div className="main-new update-contact-info-dynamic update-contact-info-dynamic--editor-v3">
                <div className="main-cotent">
                    <div className="cotent cotent-wrap update-contact-info-dynamic__container">
                        <Form {...formItemLayout} onSubmit={this.handleSubmit} className="update-contact-info-dynamic__form">
                            <Form.Item>
                                <div className="update-contact-info-dynamic__editor-grid update-contact-info-dynamic__editor-grid--double">
                                    <div className="update-contact-info-dynamic__editor-column update-contact-info-dynamic__editor-column--left">
                                        {this.renderPairGroup('UpdateContactInfoTechnicalSupport', 'technicalSupportPairs', technicalSupportPairs)}
                                    </div>
                                    <div className="update-contact-info-dynamic__editor-column update-contact-info-dynamic__editor-column--right">
                                        {this.renderPairGroup('UpdateContactInfoSalesInquiries', 'salesInquiriesPairs', salesInquiriesPairs)}
                                    </div>
                                </div>
                            </Form.Item>

                            {showPreview && (
                                <Form.Item>
                                    <div className="update-contact-info-dynamic__preview-hint">
                                        {t('UpdateContactInfoPreviewHint')}
                                    </div>
                                    <div className="update-contact-info-dynamic__preview-card">
                                        <div className={`update-contact-info-dynamic__preview-grid update-contact-info-dynamic__preview-grid--${previewColumns === '1fr 1fr' ? 'double' : 'single'}`}>
                                            {hasTsData && (
                                                <div className={`update-contact-info-dynamic__preview-column${hasSiData ? ' update-contact-info-dynamic__preview-column--with-divider' : ''}`}>
                                                    {this.renderPreviewSection(
                                                        'UpdateContactInfoTechnicalSupport',
                                                        'UpdateContactInfoTechnicalSupportDescription',
                                                        'tool',
                                                        validTechnicalSupport
                                                    )}
                                                </div>
                                            )}
                                            {hasSiData && (
                                                <div className="update-contact-info-dynamic__preview-column">
                                                    {this.renderPreviewSection(
                                                        'UpdateContactInfoSalesInquiries',
                                                        'UpdateContactInfoSalesInquiriesDescription',
                                                        'shopping-cart',
                                                        validSalesInquiries
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Form.Item>
                            )}

                            <Form.Item {...formItemLayout}>
                                <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                                    {t("Submit")}
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </div>
        );
    }
}

export default connectAlita(["serverConfig"])(
    Form.create({ name: "contactInfoDynamic" })(UpdateContactInfoDynamic)
);
